import { Link } from "@mui/material";

import { routes } from "@/global/routes";
import {
  DefaultCheckoutTerms,
  DefaultRegistrationTerms,
} from "../../default_terms_and_conditions";
import { inventoryTypeUIDs, vendorUUIDs } from "../../constants";
import {
  faqs,
  howItWorks,
  CheckoutSuccessDescription,
  AnalyticsHeader,
} from "./helpers";

import type { TeamConfig } from "../types";

import BackgroundImage from "public/images/aviators/aviators_bg.webp";
import LogoImage from "public/images/aviators/aviators_logo_jan_24.png";
import SeatMap3dPreview from "public/images/aviators/aviators_seat_map_preview.png";
import SeatMapImage from "public/images/aviators/Seating Chart Map_2026.png";

const FindGamesBanner =
  "https://ss-leagues.s3.amazonaws.com/MLB/Aviators/las-vegas-aviators-groups-banner.jpg";

const config: TeamConfig = {
  teamSchema: "aviators_groups",
  browserTitle: "Aviators Groups",
  packages: false,
  defaultRoute: routes.purchaseSeats,
  phoneNumber: "",
  teamName: "Las Vegas Aviators",

  // seat map configs
  seatMapConfigs: {
    venueId: "",
    mapId: "",
    isGroupTicketSeatMapEnabled: true,
    isView3dButtonHidden: true,
    hide3dViewFromSlider: true,
    staticSeatMapImage: SeatMapImage.src,
    customSeatMapStaticImageStyle: {
      width: "60%",
      marginX: "auto",
      display: "block",
    },
    sectionNameMapping: {
      "102": ["OFB102"],
      "103": ["OFB103"],
      "104": ["OFB104"],
      "118": ["OFB118"],
      "119": ["OFB119"],
      "120": ["OFB120"],
      "105": ["DOB105"],
      "106": ["DOB106"],
      "107": ["DOB107"],
      "108": ["DOB108"],
      "114": ["DOB114"],
      "115": ["DOB115"],
      "116": ["DOB116"],
      "117": ["DOB117"],
      "109": ["HPB109"],
      "110": ["HPB110"],
      "111": ["HPB111"],
      "112": ["HPB112"],
    },
  },

  // gift cards
  giftCardConfigs: {
    isMenuGiftCardEnabled: false,
    isLandingPageGiftCardEnabled: false,
    isStandalonePageGiftCardEnabled: false,
    redirectGiftCardUrl: routes.redeem,
  },

  // purchase credit configs
  purchaseCreditConfigs: {
    isLandingPurchaseCreditEnabled: false,
    isStandAlonePurchaseCreditEnabled: false,
    showPurchaseCreditLinkInHomeMenu: false,
    isPurchaseCreditHistoryEnabled: false,
  },

  // disabled routes
  disabledRoutes: [routes.giftCard],

  // switches
  checkoutCreditPackages: false,
  availableCreditEnabled: false,
  isCrowdHandlerEnabled: false,
  inventoryType: inventoryTypeUIDs.groupLevel,

  // purchase seats config
  purchaseSeatConfigs: {
    minGameSelectionCount: 1,
    minSeatLimit: 10,
    isSimilarSeatForAllEvents: false,
    allowManualCartInSimilar: false,
    isCheckoutEditSeatsEnabled: false,
    isDiffSeatAlertEnabled: false,
    defaultSeatCount: 10,
    checkoutTimerEnabled: false,

    isTicketTransferEnabled: true,
    hideViewTicketsInThankYouPage: true,
  },

  // feature configs
  packageConfigs: {
    isPackageEnabled: false,
    canRemovePackageGames: false,
    staticBuildYourOwn: null,
  },

  // intercom settings
  intercomConfigs: {
    isIntercomEnabled: true,
    allowedPages: [],
  },

  // checkout validations
  checkoutValidation: {
    allowedZipFailCount: Infinity,
    allowedPaymentFailCount: Infinity,
  },

  // analytics configs
  analyticsConfigs: {
    googleAnalyticsId: "",
    attrkPixelId: "",
    beaconId: "",
    AnalyticsHeader,
  },

  // redirect configs
  redirectConfigs: {
    internalRedirectUrl: "",
    externalRedirectUrl: "",
  },

  refreshInventoryWhenEventClick: false,

  isSaveCardAllowed: true,

  // urls
  accountManagerUrl: "https://am.ticketmaster.com/aviatorslv/",

  // vendor
  platform: vendorUUIDs.ticketmaster,

  // okta config
  oktaIssuer: "",
  oktaClientId: "",

  isBrainTreePaymentEnabled: false,

  // images
  images: {
    logoNameImage: "",
    faviconImage: LogoImage.src,
    authBackgroundImage: BackgroundImage.src,
    findGamesBannerImage: FindGamesBanner,
    seatMap3dPreviewImage: SeatMap3dPreview.src,
    checkoutStatusPageImage: BackgroundImage.src,
    logoImage: LogoImage.src + "?width=128&height=66",
  },

  uiConfigs: {
    bannerImage: {
      title: "",
      isBackgroundBasedBanner: false,
    },

    featuredGameCard: {
      showLogoWithTitle: false,
      hideOverlayLogo: true,
    },

    bottomBar: {
      showGameSelectionProgress: true,
    },

    gameFiltersSettings: {
      hideAccessibilityFilter: false,
      hideSectionFilter: false,
    },

    shouldDisplayServiceFee: false,
    shouldAddServiceFeeWithPerkPrice: true,

    chooseSeatsPreloaderTexts: [
      { text: "Fetching available seats…", duration: 3000 },
      { text: "Huddle up— loading your group's options…", duration: 3000 },
      { text: "Almost there—gathering seat availability…", duration: 3000 },
    ],

    customAdaLabel: (
      <>
        To purchase accessible seating locations, please contact the Box Office
        at{" "}
        <Link
          href="tel:7029437209"
          sx={{
            fontWeight: 500,
            color: "primary.main",
            textDecoration: "none",
          }}
        >
          (702) 943-7209
        </Link>
        .
      </>
    ),
  },

  colors: {
    primary: "#022243",
  },

  howItWorks: howItWorks,
  faqs: faqs,

  languages: {
    topNavMenuItems: null,
    checkoutTermsAndCondition: <DefaultCheckoutTerms />,
    registrationTermsAndCondition: <DefaultRegistrationTerms />,
    purchaseSeats: {
      progressBarAditionalText: " to continue!",
      progressBarSuccessText:
        "You're good to go! Select Continue to view seating options.",
    },
    purchaseGiftCard: null,
    redeemGiftCard: null,

    checkoutSuccessMessage: "Your purchase is complete!",
    checkoutSuccessDescription: <CheckoutSuccessDescription />,

    gameFilters: {
      seatCounter: {
        description:
          "Select the number of people in you group. Minimum of 10 required",
      },
      priceRange: {
        title: "Price Range per Ticket",
      },
      accessibility: {
        description:
          "If you are looking for accessible seating for your group, please contact the box office at (303) 762-5437.",
      },
    },

    giftCard: {
      title: "",
      createGiftCard: {
        description: "",
        recipient: {
          sendNowFalse: {
            description: "",
          },
          sendNowTrue: {
            description: "",
          },
        },
      },
      redeemGiftCard: {
        loggedIn: {
          description: "",
        },
        logout: {
          description: "",
        },
        success: {
          description: "",
        },
      },
    },

    signInAuthText:
      "Connect your Ticketmaster account to continue with seamless access.",
  },
};

export default config;
